/**
 * Estado de cada card da página de Planos: ação, texto e bloqueio do botão e avisos.
 * Regras que antes ficavam soltas no Plans.js, sem mudança de comportamento.
 */

export const ROLE_BLOCKED_TEXT = 'Só a pessoa responsável pela conta ou um administrador pode escolher o plano.';
export const DOWNGRADE_BLOCKED_TEXT = 'Para evitar conflitos com usuários, profissionais e agendas já cadastrados, a troca para um plano inferior ainda não está disponível automaticamente.';
export const CANCELING_BLOCKED_TEXT = 'Sua assinatura está em cancelamento. Desfaça o cancelamento para trocar de plano.';

/**
 * Ação disponível para um plano dado o plano atual do assinante.
 * @returns {'checkout'|'current'|'upgrade'|'downgrade'}
 */
export function getPlanAction(plan, currentPlan) {
  if (!currentPlan) return 'checkout';
  if (plan.id === currentPlan.id) return 'current';
  if (plan.displayOrder > currentPlan.displayOrder) return 'upgrade';
  return 'downgrade';
}

/** Plano em destaque ("Mais escolhido"): o do meio com 3+ planos, senão o primeiro. */
export function getFeaturedPlanIndex(plans) {
  if (plans.length === 0) return -1;
  if (plans.length <= 2) return 0;
  return Math.floor((plans.length - 1) / 2);
}

/**
 * @param {Object} plan
 * @param {Object} ctx  — isSubscriber, subscription, currentPlan, processingPlanId,
 *                        canActivateSubscription, canDowngrade, isCanceling,
 *                        minimumAllowedPlanOrder, hadSubscription
 */
export function getPlanCardState(plan, ctx) {
  const {
    isSubscriber,
    subscription,
    currentPlan,
    processingPlanId,
    canActivateSubscription,
    canDowngrade,
    isCanceling,
    minimumAllowedPlanOrder,
    hadSubscription,
  } = ctx;

  const hasActivePlan = !!subscription?.has_active_plan;
  const subscriberWithPlan = Boolean(isSubscriber && hasActivePlan);
  const isProcessing = processingPlanId === plan.id;
  const isCurrent = Boolean(subscriberWithPlan && plan.id === subscription.subscription_plan_id);
  const action = subscriberWithPlan ? getPlanAction(plan, currentPlan) : 'checkout';
  const isUpgrade = action === 'upgrade';
  const isDowngrade = action === 'downgrade';
  // Sem plano ativo, abaixo do último plano assinado: troca bloqueada
  const isDowngradeBlocked =
    !hasActivePlan && minimumAllowedPlanOrder !== null && plan.displayOrder < minimumAllowedPlanOrder;

  const ctaText = (() => {
    if (isProcessing) return 'Processando...';
    if (subscriberWithPlan) {
      if (action === 'current') return 'Plano em uso';
      // "Sob consulta": nunca vira upgrade automático
      if (plan.contact_sales) return 'Falar com a gente';
      if (isUpgrade) return `Fazer upgrade para ${plan.name}`;
      if (isDowngrade) return `Mudar para ${plan.name}`;
    }
    if (plan.contact_sales) return 'Falar com a gente';
    // "Continuar" só para quem já assinou; trial (ativo ou vencido) escolhe o primeiro plano
    if (hadSubscription) return 'Continuar com a Gabriela';
    return 'Escolher plano';
  })();

  const ctaDisabled = (() => {
    if (isProcessing) return true;
    // "Sob consulta": o botão só abre o WhatsApp, fica ativo para todo mundo,
    // exceto para quem já está nesse plano.
    if (plan.contact_sales) return Boolean(subscriberWithPlan && action === 'current');
    if (!canActivateSubscription) return true;
    if (plan.is_active === 0) return true;
    if (subscriberWithPlan) {
      if (action === 'current') return true;
      if (isCanceling) return true;
      if (isDowngrade && !canDowngrade) return true;
    }
    if (isDowngradeBlocked) return true;
    return false;
  })();

  const notes = [];
  if (isDowngradeBlocked) notes.push(DOWNGRADE_BLOCKED_TEXT);
  if (isCanceling && (isUpgrade || isDowngrade) && canActivateSubscription && !plan.contact_sales) {
    notes.push(CANCELING_BLOCKED_TEXT);
  }
  const roleBlocked = !canActivateSubscription && !plan.contact_sales && !isCurrent;
  if (roleBlocked) notes.push(ROLE_BLOCKED_TEXT);

  return {
    isProcessing,
    isCurrent,
    isUpgrade,
    isDowngrade,
    ctaText,
    ctaDisabled,
    // Cadeado no botão: bloqueado por papel ou por troca para plano inferior
    ctaLocked: roleBlocked || isDowngradeBlocked,
    notes,
  };
}
